import apiClient from '@/services/api_client';
import { ChatSession, ChatSessionSearchResponse } from '@/types';

interface UpdateChatSessionOptions {
    chatSessionId: string;
    tenantId: string;
    name?: string;
    firstVisit?: boolean;
    documentId?: string;
    customAgentId?: string;
    /** Empty string detaches the session from its project. */
    projectId?: string;
}
export interface CreateChatSessionOptions {
    name: string;
    tenantId: string;
    documentId?: string;
    customAgentId?: string;
    /** Groups the session under a project, whether it is a chat or a studio session. */
    projectId?: string;
}

export async function createChatSession(options: CreateChatSessionOptions) {
    const url = '/chatsessions';
    const body: Record<string, unknown> = {
        name: options.name,
        tenant_id: options.tenantId
    };
    if (options.documentId !== undefined) body.document_id = options.documentId;
    if (options.customAgentId !== undefined)
        body.custom_agent_id = options.customAgentId;
    if (options.projectId !== undefined) body.project_id = options.projectId;
    return await apiClient.post(url, body);
}

export async function getChatSessions(
    tenantId: string
): Promise<ChatSession[]> {
    const url = '/chatsessions?tenant_id=' + tenantId;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getChatSessionById(
    chatSessionId: string,
    tenantId: string
): Promise<ChatSession> {
    const url = `/chatsessions/${chatSessionId}?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function archiveChatSession(
    chatSessionId: string,
    tenantId: string
) {
    const url = '/chatsessions/archive';
    await apiClient.patch(url, {
        chat_session_id: chatSessionId,
        tenant_id: tenantId
    });
}

export async function updateChatSession(options: UpdateChatSessionOptions) {
    const url = '/chatsessions/update';
    const body: Record<string, unknown> = {
        chat_session_id: options.chatSessionId,
        tenant_id: options.tenantId
    };
    if (options.name !== undefined) body.name = options.name;
    if (options.firstVisit !== undefined) body.first_visit = options.firstVisit;
    if (options.documentId !== undefined) body.document_id = options.documentId;
    if (options.customAgentId !== undefined)
        body.custom_agent_id = options.customAgentId;
    if (options.projectId !== undefined) body.project_id = options.projectId;

    return await apiClient.post(url, body);
}

export interface ResolveSidebarTitleResponse {
    name: string | null;
    chat_session_id: string;
    document_id: string | null;
    updated_chat_session: boolean;
    updated_document: boolean;
}

export async function resolveSidebarTitle(
    chatSessionId: string,
    tenantId: string,
    firstMessage: string
): Promise<ResolveSidebarTitleResponse> {
    return await apiClient.post('/chatsessions/resolve_sidebar_title', {
        chat_session_id: chatSessionId,
        tenant_id: tenantId,
        first_message: firstMessage
    });
}

export async function addFileMessage(
    chatSessionId: string,
    tenantId: string,
    fileName: string
) {
    const url = '/chatsessions/add_file_message';
    return await apiClient.post(url, {
        chat_session_id: chatSessionId,
        tenant_id: tenantId,
        file_name: fileName
    });
}

export async function addMessageToMemory(
    chatSessionId: string,
    tenantId: string,
    message: string,
    type: 'human' | 'ai' = 'human'
) {
    const url = '/chatsessions/add_message_to_memory';
    const response = await apiClient.post(url, {
        chat_session_id: chatSessionId,
        tenant_id: tenantId,
        message: message,
        type: type
    });
    return response;
}

export async function searchChatSessions(
    tenantId: string,
    options?: { search?: string; offset?: number; limit?: number },
    signal?: AbortSignal
): Promise<ChatSessionSearchResponse> {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (options?.search?.trim()) params.set('search', options.search.trim());
    if (options?.offset !== undefined)
        params.set('offset', String(options.offset));
    if (options?.limit !== undefined)
        params.set('limit', String(options.limit));

    const response = await apiClient.get(`/chatsessions/search?${params}`, {
        signal
    });
    return response.json();
}

export const getMessages = async (
    sessionId: string,
    tenantId: string,
    startIndex: number,
    numMessagesToFetch: number
) => {
    const url = `/messages?num_messages=${numMessagesToFetch}&start_index=${startIndex}&session_id=${sessionId}&tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
};
